import type { Clip, Track } from '@mimir/primitive/premiere/sequenceDetails';
import { MediaType } from '@mimir/primitive/premiere/sequenceDetails';
import { v1 as uuidv1 } from 'uuid';
import { replaceAll } from '../util/general';
import { invariant } from '../util/invariant';
import compact from 'lodash/compact';
import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';
import * as xpath from 'xpath';
import { DOMParser, XMLSerializer } from '@xmldom/xmldom';
import type { Fraction } from '../util/fractionType';
import { simplifyFraction } from '../util/integerMath';
import uniq from 'lodash/uniq';
import uniqBy from 'lodash/uniqBy';

/**
 * Represents the XML structure contained within a Adobe Premiere .prproj
 * project file.
 *
 * Can load and save .prproj files and can inspect and modify its contents. This
 * is useful for annotating project items referencing media with Mimir item
 * identifirers, and to rewrite the local file paths of media referenced in the
 * project.
 */
export class PrprojDocument {
  private mimirItemIdByMediaPath: Record<string, string> = {};
  private freeId: number | null = null;

  constructor(public doc: Document) {
  }

  toXml(): string {
    // the Project.Metadata.Schema has some escaped chars that do not come back exactly as
    // Premiere expects when passed through XMLSerializer. So we remember the original, mark its location,
    // and replace back at the end
    const verbatimProjectMetadataSchemaKey = '_mimirVerbatimProjectMetadataSchema_';
    let verbatimProjectMetadataSchemaValue;
    const nodes = xpath.select('//Project/Node/Properties/Project.Metadata.Schema', this.doc) as Node[];
    if (!isEmpty(nodes)) {
      const node = nodes[0];

      // save
      verbatimProjectMetadataSchemaValue = node.textContent?.replace(/\n/g, '&#10;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

      // mark with special string
      node.textContent = verbatimProjectMetadataSchemaKey;
    }

    let prprojXmlString = new XMLSerializer().serializeToString(this.doc) + '\n\n';
    const genHeader = '<?xml version="1.0" encoding="UTF-8"?>';
    const wantHeader = '<?xml version="1.0" encoding="UTF-8" ?>';
    if (prprojXmlString.startsWith(genHeader)) {
      prprojXmlString = wantHeader + prprojXmlString.substr(genHeader.length);
    }

    // replace back original Project.Metadata.Schema
    if (verbatimProjectMetadataSchemaValue) {
      prprojXmlString = replaceAll(prprojXmlString, verbatimProjectMetadataSchemaKey, verbatimProjectMetadataSchemaValue);
    }

    invariant(prprojXmlString.indexOf('e1cbe69d') == -1, 'should have been replaced by newName');
    return prprojXmlString;
  }

  // WARNING: This function is only used for e2e test and only changes the first sequence in the project.
  // This should not be used for actual use cases where there can be multiple sequences in the prproj.
  applySequenceAndTapeName(newName: string, tapeName: string) {
    const sequences = xpath.select('/PremiereData/Sequence/Name', this.doc) as Node[];
    if (!sequences[0]) {
      throw new Error('No /PremiereData/Sequence/Name');
    }
    const oldSequenceName = sequences[0].textContent;
    sequences[0].textContent = newName;
    const masterClips = xpath.select('/PremiereData/MasterClip', this.doc) as Node[];
    for (const mc of masterClips) {
      const mcn = xpath.select('Name', mc, true) as Node;
      if (!mcn) {
        throw new Error('No Name');
      }
      if (mcn.textContent == oldSequenceName) {
        mcn.textContent = newName;
        const loggingInfoRef = xpath.select('LoggingInfo/@ObjectRef', mc, true) as Attr;
        const cliId = loggingInfoRef.value;

        const clis = xpath.select('/PremiereData/ClipLoggingInfo', this.doc) as Node[];
        for (const cli of clis) {
          const oid = xpath.select('@ObjectID', cli, true) as Attr;
          if (oid.value == cliId) {
            const cliCn = xpath.select('ClipName', cli, true) as Node;
            if (cliCn) {
              // Not all ClipLoggingInfo instances have ClipName.
              cliCn.textContent = newName;
            }

            const cliTn = xpath.select('TapeName', cli, true) as Node;
            if (cliTn) {
              cliTn.textContent = tapeName;
            } else {
              // Not all ClipLoggingInfo instances have TapeName. Need to create one.
              const tnTextNode = this.doc.createTextNode(tapeName);
              const tnElement = this.doc.createElement('TapeName');
              tnElement.appendChild(tnTextNode);
              cli.appendChild(this.doc.createTextNode('\t'));
              cli.appendChild(tnElement);
              cli.appendChild(this.doc.createTextNode('\n\t'));
            }
          }
        }
      }
    }
  }

  private objectElsById: Record<string, Element> | undefined;

  getElementById(id: string): Element {
    if (!this.objectElsById) {
      const objectEls = xpath.select('/PremiereData/*[@ObjectID]', this.doc) as Element[];
      invariant(objectEls.length > 0, 'expected object elements');
      this.objectElsById = keyBy(objectEls, obj => obj.getAttribute('ObjectID') as string);
    }

    const el = this.objectElsById[id];
    if (el == null) {
      throw new Error('No element with id ' + id);
    }

    return el;
  }

  getElementByRefElement(refElement: Element): Element {
    const objectRef = refElement.getAttribute('ObjectRef');
    if (objectRef) {
      return this.getElementById(objectRef);
    }
    const objectURef = refElement.getAttribute('ObjectURef');
    if (objectURef) {
      return this.getElementByUid(objectURef);
    }
    throw new Error('Reference element has no ObjectRef nor ObjectURef');
  }

  cloneElement(element: Element): Element {
    const clone = element.cloneNode(true) as Element;
    const objectId = clone.getAttribute('ObjectID');
    if (objectId) {
      const newObjectId = this.getFreeObjectId();
      clone.setAttribute('ObjectID', newObjectId);
      if (this.objectElsById) {
        this.objectElsById[newObjectId] = clone;
      }
    }
    const objectUid = clone.getAttribute('ObjectUID');
    if (objectUid) {
      const newObjectUid = uuidv1();
      clone.setAttribute('ObjectUID', newObjectUid);
      if (this.objectElsByUid) {
        this.objectElsByUid[newObjectUid] = clone;
      }
    }
    const premiereData = xpath.select('/PremiereData', this.doc, true) as Element;
    premiereData.appendChild(clone);
    return clone;
  }

  linkElement(refElement: Element, targetElement: Element) {
    const objectId = targetElement.getAttribute('ObjectID');
    const currentRef = refElement.getAttribute('ObjectRef');
    if (objectId && currentRef) {
      refElement.setAttribute('ObjectRef', objectId);
    }
    const objectUid = targetElement.getAttribute('ObjectUID');
    const currentUref = refElement.getAttribute('ObjectURef');
    if (objectUid && currentUref) {
      refElement.setAttribute('ObjectURef', objectUid);
    }
  }

  /**
    * This function will do the following:
    *   1)  Look up the original Element referenced by either an ObjectRef or
    *       an ObjectURef on the refElement
    *   2)  Do a deep clone of the original element and add the cloned element
    *       to the PremiereData root element
    *   3)  Bump the ObjectID/ObjectUID to a free value for the cloned element
    *   4)  Update the reference attribute on refElement to point to the new
    *       clone.
    */
  cloneAndRelinkReference(refElement: Element) {
    const original = this.getElementByRefElement(refElement);
    const clone = this.cloneElement(original);
    this.linkElement(refElement, clone);
    return clone;
  }

  private objectElsByUid: Record<string, Element> | undefined;

  getElementByUid(uid: string): Element {
    if (!this.objectElsByUid) {
      const objectUidEls = xpath.select('/PremiereData/*[@ObjectUID]', this.doc) as Element[];
      invariant(objectUidEls.length > 0, 'expected objectUidEls');
      this.objectElsByUid = keyBy(objectUidEls, getObjectUid);
    }

    const el = this.objectElsByUid[uid] ?? null;

    if (el == null) {
      throw new Error('No element with uid ' + uid);
    }

    return el;
  }

  getSingleReferencedIdElement(node: Node, uniqueRefElementPath: string): Element {
    const ref = xpath.select(uniqueRefElementPath + '/@ObjectRef', node, true) as Attr;
    if (!ref) {
      throw new Error('Missing ' + uniqueRefElementPath + ' ID reference for ' + node);
    }
    return this.getElementById(ref.value);
  }

  getAllReferencedIdElements(node: Node, refElementPath: string): Element[] {
    const refs = xpath.select(refElementPath + '/@ObjectRef', node) as Attr[];
    return refs.map(ref => this.getElementById(ref.value));
  }

  getSingleReferencedUidElement(node: Node, uniqueRefElementPath: string): Element {
    return this.getElementByUid(getSingleURef(node, uniqueRefElementPath));
  }

  getOptionalReferencedUidElement(node: Node, uniqueRefElementPath: string): Element | null {
    const ref = xpath.select(uniqueRefElementPath + '/@ObjectURef', node, true) as Attr;
    if (!ref) {
      return null;
    }
    return this.getElementByUid(ref.value);
  }

  getAllReferencedUidElements(node: Node, refElementPath: string): Element[] {
    const refs = xpath.select(refElementPath + '/@ObjectURef', node) as Attr[];
    return refs.map(ref => this.getElementByUid(ref.value));
  }

  getAllClipProjectItems() {
    return xpath.select('/PremiereData/ClipProjectItem', this.doc) as Element[];
  }

  cloneClipProjectItem(cpi: Element): Element {
    const clone = this.cloneElement(cpi);
    const originalMasterClipRef = xpath.select('MasterClip', clone, true) as Element;
    invariant(originalMasterClipRef, 'Expected originalMasterClipRef');
    const originalMasterClip = this.getElementByRefElement(originalMasterClipRef);
    const masterClipClone = this.cloneMasterClip(originalMasterClip);
    this.linkElement(originalMasterClipRef, masterClipClone);
    return clone;
  }

  getMasterClipForProjectItem(cpi: Element): Element {
    const masterClipRef = xpath.select('MasterClip', cpi, true) as Element;
    return this.getElementByRefElement(masterClipRef);
  }

  getAllSequences() {
    return xpath.select('/PremiereData/Sequence', this.doc) as Element[];
  }

  setMimirItemIdForMediaPath(filePathForScript: string , newMimirItemId: string) {
    const clipProjectItems = this.getAllClipProjectItems();
    let found = false;
    for (const cpi of clipProjectItems) {
      const mc = this.getSingleReferencedUidElement(cpi, 'MasterClip');
      const clipRefs = xpath.select('Clips/Clip/@ObjectRef', mc) as Attr[];

      /*
        We assume that for the MasterClip elements that we care about both
        referenced clips will lead to the same Media element.
      */
      const clip = this.getElementById(clipRefs[0].value);
      const ms = this.getSingleReferencedIdElement(clip, 'Clip/Source');

      const mediaRef = xpath.select('MediaSource/Media/@ObjectURef', ms, true) as Attr | undefined;
      if (!mediaRef) {
        // Not all media sources have direct media. We don't care about those
        // that don't.
        continue;
      }
      const media = this.getElementByUid(mediaRef.value);

      const filePathEl = xpath.select('FilePath', media, true) as Element;
      const filePath = filePathEl.textContent;
      if (!filePath) {
        throw new Error('Missing file path for ' + media);
      }

      if (filePath == filePathForScript) {
        found = true;
        const propertiesEl = getClipProjectItemProperties(cpi);
        const mimirItemIdEls = xpath.select('Mimir.Item.Id', cpi) as Element[];
        let mimirItemIdEl;
        if (mimirItemIdEls.length == 0) {
          mimirItemIdEl = this.doc.createElement('Mimir.Item.Id');
          propertiesEl.appendChild(mimirItemIdEl);
        } else {
          mimirItemIdEl = mimirItemIdEls[0];
        }

        mimirItemIdEl.textContent = newMimirItemId;
        this.mimirItemIdByMediaPath[filePathForScript] = newMimirItemId;
      }
    }

    if (!found) {
      throw new Error('No ClipProjectItem referencing ' + filePathForScript + ' found.');
    }
  }

  getAllMediaElements(): Element[] {
    return xpath.select('/PremiereData/Media', this.doc) as Element[];
  }

  getAllStreamElements(): Element[] {
    return xpath.select('/PremiereData/*[self::AudioStream or self::VideoStream]', this.doc) as Element[];
  }

  getAllSubClips(): Element[] {
    return xpath.select('/PremiereData/SubClip', this.doc) as Element[];
  }

  getFilePathElementsForAllMedia(): Element[] {
    return this.getAllMediaElements().map(mel => getClipFilePathText(mel));
  }

  getMediaForMasterClip(masterClipElement: Element): Element | null {
    const clipRefs = xpath.select('Clips/Clip', masterClipElement) as Element[];
    invariant(clipRefs.length > 0, 'No clips associated with MasterClip');
    const firstClipRef = clipRefs[0];
    const firstClip = this.getElementByRefElement(firstClipRef);
    const sourceRef = xpath.select('Clip/Source', firstClip, true) as Element;
    invariant(sourceRef, 'Expected sourceRef');
    const source = this.getElementByRefElement(sourceRef);
    const mediaRef = xpath.select('MediaSource/Media', source, true) as Element;
    if (!mediaRef) {
      return null;
    }
    invariant(mediaRef, 'Expected mediaRef');
    return this.getElementByRefElement(mediaRef);
  }

  getMediaFrameRate(media: Element): Fraction {
    const videoStreamRef = (xpath.select('VideoStream/@ObjectRef', media, true) as Attr).value;
    const videoStream = this.getElementById(videoStreamRef);
    const frameRateElement = xpath.select('FrameRate', videoStream, true) as Element;
    const denominator = parseInt(frameRateElement.textContent ?? '');
    invariant(isFinite(denominator), 'Failed to extract frame rate');
    const numerator = premiereTicksPerSecond;
    return simplifyFraction({ numerator, denominator });
  }

  private trackItemToClip(trackItem: Element, key: string): Clip {
    const clipTrackItem = xpath.select('ClipTrackItem', trackItem, true) as Element;

    const start = parsePremiereTimeSpan(clipTrackItem, 'TrackItem/Start');
    const end = parsePremiereTimeSpan(clipTrackItem, 'TrackItem/End');

    const subclipEl = this.getSingleReferencedIdElement(clipTrackItem, 'SubClip');
    const clipEl = this.getSingleReferencedIdElement(subclipEl, 'Clip');
    const clipId = clipEl.getAttribute('ObjectID');
    const inPoint = parsePremiereTimeSpan(clipEl, 'Clip/InPoint');
    const outPoint = parsePremiereTimeSpan(clipEl, 'Clip/OutPoint');

    const sourceEl = this.getSingleReferencedIdElement(clipEl, 'Clip/Source');
    const mediaEl = this.getSingleReferencedUidElement(sourceEl, 'MediaSource/Media');
    const filePathText = getClipFilePathText(mediaEl);
    const mediaPath = filePathText.textContent;


    const clip: Clip = {
      start,
      end,
      duration: end - start,
      inPoint,
      outPoint,
      key: `${key}:${clipId}`
    };
    if (mediaPath) {
      clip.mediaPath = mediaPath;
      const mimirItemId = this.mimirItemIdByMediaPath[mediaPath];
      if (mimirItemId) {
        clip.mimirItemId = mimirItemId;
      }
    }
    return clip;
  }

  private trackElementToTrackOrNull(track: Element, trackType: TrackType, key: string) {
    const idText = xpath.select('ClipTrack/Track/ID', track, true) as Node;
    const id = parseInt(idText.textContent ?? '0');
    const indexText = xpath.select('ClipTrack/Track/Index', track, true) as Node;
    const index = parseInt(indexText.textContent ?? '0');
    const ordinal = index + 1;


    const trackItemEls = this.getAllReferencedIdElements(track, 'ClipTrack/ClipItems/TrackItems/TrackItem');
    const clips: Clip[] = trackItemEls.map(ti => this.trackItemToClip(ti, key));
    if (clips.length == 0) {
      return null;
    }

    return {
        id: id,
        clips: clips,
        trackLabel: trackType.labelPrefix + ordinal,
        name: trackType.namePrefix + ordinal,
        mediaType: trackType.mediaType
    };
  }

  public trackGroupToTracks(trackGroup: Element, key: string): Track[] {
    const trackType = trackTypesByGroupTag[trackGroup.tagName];
    const trackEls = this.getAllReferencedUidElements(trackGroup, 'TrackGroup/Tracks/Track');
    return compact(trackEls.map(track => this.trackElementToTrackOrNull(track, trackType, key)));
  }

  public getFirstSequenceElement() {
    return xpath.select('/PremiereData/Sequence', this.doc, true) as Element;
  }

  public getSequenceItems(sequenceElement: Element): Element[] {
    return (xpath.select('PersistentGroupContainer/LinkContainer/Links/Link/@ObjectRef', sequenceElement) as Attr[])
      .map((attr) => attr.value)
      .map((objectRef) => this.getElementById(objectRef));
  }

  public getTrackItemsForSequenceItem(sequenceItemElement: Element): Element[] {
    return (xpath.select('TrackItemGroup/TrackItems/TrackItem/@ObjectRef', sequenceItemElement) as Attr[])
      .map((attr) => attr.value)
      .map((objectRef) => this.getElementById(objectRef));
  }

  public getMasterClipForSequenceItem(sequenceItemElement: Element): Element {
    const subClipRefs = this.getTrackItemsForSequenceItem(sequenceItemElement)
      .map((trackItem) => (xpath.select('ClipTrackItem/SubClip/@ObjectRef', trackItem, true) as Attr))
      .map((attr) => attr.value);

    invariant(subClipRefs.length > 0, 'No subclips for sequence item');

    const masterClipURefs = subClipRefs
      .map((objectRef) => this.getElementById(objectRef))
      .map((subClip) => xpath.select('MasterClip/@ObjectURef', subClip, true) as Attr)
      .map((attr) => attr.value);

    const uniqueMasterClipURefs = uniq(masterClipURefs);
    if (uniqueMasterClipURefs.length === 0) {
      throw new Error('No MasterClip found for sequence item');
    }
    if (uniqueMasterClipURefs.length > 1) {
      throw new Error('No MasterClip has more than one master clip reference');
    }
    return this.getElementByUid(uniqueMasterClipURefs[0]);
  }

  public cloneMasterClip(masterClipElement: Element): Element {
    const masterClipClone = this.cloneElement(masterClipElement);

    const clipRefs = xpath.select('Clips/Clip', masterClipClone) as Element[];
    invariant(clipRefs.length > 0, 'No clipRefs for MasterClip');

    const mediaRefMap : Record<string, Element> = { };
    for (const clipRef of clipRefs) {
      const clipClone = this.cloneAndRelinkReference(clipRef);
      clipClone.setAttribute('ClipID', uuidv1());

      const sourceRef = xpath.select('Clip/Source', clipClone, true) as Element;
      invariant(sourceRef, 'expected sourceRef');
      const sourceClone = this.cloneAndRelinkReference(sourceRef);

      const mediaRef = xpath.select('MediaSource/Media', sourceClone, true) as Element;
      const oldMediaURef = mediaRef.getAttribute('ObjectURef');
      invariant(oldMediaURef !== null, 'Expected oldMediaURef');

      let mediaClone : Element | undefined = mediaRefMap[oldMediaURef];
      if (mediaClone === undefined) {
        const mediaElement = this.getElementByUid(oldMediaURef);
        invariant(mediaElement, 'Expected mediaElement');
        mediaClone = this.cloneElement(mediaElement);

        const audioStreamRef = xpath.select('AudioStream', mediaClone, true) as Element;
        this.cloneAndRelinkReference(audioStreamRef);

        const videoStreamRef = xpath.select('VideoStream', mediaClone, true) as Element;
        this.cloneAndRelinkReference(videoStreamRef);

        mediaRefMap[oldMediaURef] = mediaClone;
      }

      const secondaryContentItemRefs = xpath.select('SecondaryContents/SecondaryContentItem', clipClone) as Element[];
      for (const secondaryContentItemRef of secondaryContentItemRefs) {
        const secondaryContentItemClone = this.cloneAndRelinkReference(secondaryContentItemRef);
        const content = xpath.select('Content', secondaryContentItemClone, true) as Element;
        this.linkElement(content, sourceClone);
      }

      this.linkElement(mediaRef, mediaClone);
    }

    return masterClipClone;
  }

  public setMasterClipForTrackItem(trackItemElement: Element, masterClipElement: Element) {
    const subClipRef = xpath.select('ClipTrackItem/SubClip', trackItemElement, true) as Element;
    invariant(subClipRef, 'Expected subClipRef');
    const subClipElement = this.getElementByRefElement(subClipRef);

    // First we need to build up a map from the original MasterClips sourceRefs to
    // the source ref index so that we can pick the correct source ref from the new
    // master clip
    const originalSourceRefToIndex : Record<string, string> = { };
    const originalMasterClipURef = (xpath.select('MasterClip/@ObjectURef', subClipElement, true) as Attr).value;
    const originalMasterClipElement = this.getElementByUid(originalMasterClipURef);
    const originalClipRefs = xpath.select('Clips/Clip', originalMasterClipElement) as Element[];
    {
      for (const clipRef of originalClipRefs) {
        const index = clipRef.getAttribute('Index');
        invariant(index, 'Expected index');
        const clipElement = this.getElementByRefElement(clipRef);
        const sourceRef = (xpath.select('Clip/Source/@ObjectRef', clipElement, true) as Attr).value;
        originalSourceRefToIndex[sourceRef] = index;
      }
    }

    // Then we build up a map from Index to SourceRef for the new MasterClip
    const indexToNewSourceRef : Record<string, string> = { };
    const clipRefs = xpath.select('Clips/Clip', masterClipElement) as Element[];
    {
      for (const clipRef of clipRefs) {
        const index = clipRef.getAttribute('Index');
        invariant(index, 'Expected index');
        const clipElement = this.getElementByRefElement(clipRef);
        const sourceRef = (xpath.select('Clip/Source/@ObjectRef', clipElement, true) as Attr).value;
        indexToNewSourceRef[index] = sourceRef;
      }
    }

    {
      const clipRef = xpath.select('Clip', subClipElement, true) as Element;
      const clipElement = this.getElementByRefElement(clipRef);

      const sourceRef = xpath.select('Clip/Source', clipElement, true) as Element;
      const index = originalSourceRefToIndex[sourceRef.getAttribute('ObjectRef') ?? ''];
      invariant(index, `Originally referenced master clip does not contain source ref: ${sourceRef.getAttribute('ObjectRef')}`);
      const sourceElement = this.getElementById(indexToNewSourceRef[index]);
      this.linkElement(sourceRef, sourceElement);

      const secondaryContentItemRefs = xpath.select('SecondaryContents/SecondaryContentItem', clipElement) as Element[];
      for (const secondaryContentItemRef of secondaryContentItemRefs) {
        const secondaryContentItemElement = this.cloneAndRelinkReference(secondaryContentItemRef);
        const content = xpath.select('Content', secondaryContentItemElement, true) as Element;
        this.linkElement(content, sourceElement);
      }
    }

    const masterClipURef = xpath.select('MasterClip', subClipElement, true) as Element;
    this.linkElement(masterClipURef, masterClipElement);
  }

  public setMediaFilePathForMasterClip(masterClipElement: Element, mediaFilePath: string) {
    const clipRefs = xpath.select('Clips/Clip', masterClipElement) as Element[];
    const mediaElements : Element[] = [];
    for (const clipRef of clipRefs) {
      const clip = this.getElementByRefElement(clipRef);
      const sourceRef = xpath.select('Clip/Source', clip, true) as Element;
      const source = this.getElementByRefElement(sourceRef);

      const mediaRef = xpath.select('MediaSource/Media', source, true) as Element;
      const media = this.getElementByRefElement(mediaRef);
      mediaElements.push(media);
    }

    for (const media of uniqBy(mediaElements, (e) => e.getAttribute('ObjectUID'))) {
      const filePath = xpath.select('FilePath', media, true) as Element;
      filePath.textContent = mediaFilePath;
      const actualMediaFilePath = xpath.select('ActualMediaFilePath', media, true) as Element;
      actualMediaFilePath.textContent = mediaFilePath;
      const relativePath = xpath.select('RelativePath', media, true) as Element;
      if (relativePath) {
        media.removeChild(relativePath);
      }

      const title = xpath.select('Title', media, true) as Element;
      title.textContent = `(CLONE) ${title.textContent}`;
    }
  }

  private getFreeObjectId(): string {
    if (this.freeId === null) {

      const allObjectIds : number[] = (xpath.select('//*/@ObjectID', this.doc) as Attr[]).map((attr) => parseInt(attr.value));
      allObjectIds.sort((a, b) => a - b);
      this.freeId = allObjectIds[allObjectIds.length - 1];
    }
    this.freeId += 1;
    return this.freeId.toString();
  }

  public findTrackForTrackItem(trackItemElement: Element): Element | null {
    const objectId = trackItemElement.getAttribute('ObjectID');
    invariant(objectId, 'Expected TrackItem element to have ObjectID attribute');

    const allTracks = xpath.select('/PremiereData/*[self::VideoClipTrack or self::AudioClipTrack]', this.doc) as Element[];

    for (const track of allTracks) {
      const trackItems = xpath.select('ClipTrack/ClipItems/TrackItems/TrackItem', track) as Element[];
      const hasTrack = trackItems.map((el) => el.getAttribute('ObjectRef'))
        .some((objectRef) => objectRef === objectId);

      if (hasTrack) {
        return track;
      }
    }

    return null;
  }

  public addTrackItemToTrack(trackElement: Element, trackItemElement: Element) {
    const objectId = trackItemElement.getAttribute('ObjectID');
    invariant(objectId, 'Expected TrackItem element to have ObjectID attribute');

    const trackItems = xpath.select('ClipTrack/ClipItems/TrackItems', trackElement, true) as Element;
    const indices = (xpath.select('TrackItem', trackItems) as Element[])
      .map((t) => parseInt(t.getAttribute('Index') ?? ''));

    indices.sort((a, b) => a - b);
    const highestIndex = indices[indices.length - 1];
    const nextIndex = highestIndex + 1;

    const trackItem = this.doc.createElement('TrackItem');
    trackItem.setAttribute('Index', `${nextIndex}`);
    trackItem.setAttribute('ObjectRef', objectId);
    trackItems.appendChild(trackItem);
  }

  public cloneTrackItem(trackItemElement: Element): Element {
    const clonedTrackItem : Element = this.cloneElement(trackItemElement);

    const components = xpath.select('ClipTrackItem/ComponentOwner/Components', clonedTrackItem, true) as Element;
    invariant(components, 'Expected components');
    this.cloneAndRelinkReference(components);

    const subClipRef : Element = xpath.select('ClipTrackItem/SubClip', clonedTrackItem, true) as Element;
    invariant(subClipRef, 'Expected subClipRef');
    const subClipClone = this.cloneAndRelinkReference(subClipRef);

    const clipRef = xpath.select('Clip', subClipClone, true) as Element;
    invariant(clipRef, 'Expected clipRef');
    const clipClone = this.cloneAndRelinkReference(clipRef);

    const clipIdNode = xpath.select('Clip/ClipID', clipClone, true) as Element;
    invariant(clipIdNode, 'Expected clipId');
    clipIdNode.textContent = uuidv1();

    const track = this.findTrackForTrackItem(trackItemElement);
    invariant(track, 'Expected track');

    this.addTrackItemToTrack(track, clonedTrackItem);

    return clonedTrackItem;
  }

  public getTrackItemInPoint(trackItem: Element): number {
    const startElement = xpath.select('ClipTrackItem/TrackItem/Start', trackItem, true) as Element;
    const ticks = parseInt(startElement.textContent ?? '');
    invariant(isFinite(ticks), 'expected ticks to be a valid integer');
    return ticks / premiereTicksPerSecond;
  }

  public getTrackItemOutPoint(trackItem: Element): number {
    const startElement = xpath.select('ClipTrackItem/TrackItem/End', trackItem, true) as Element;
    const ticks = parseInt(startElement.textContent ?? '');
    invariant(isFinite(ticks), 'expected ticks to be a valid integer');
    return ticks / premiereTicksPerSecond;
  }

  public setTrackItemInPoint(trackItem: Element, inPoint: number) {
    const startElement = xpath.select('ClipTrackItem/TrackItem/Start', trackItem, true) as Element;
    startElement.textContent = `${Math.round(inPoint * premiereTicksPerSecond)}`;
  }

  public setTrackItemOutPoint(trackItem: Element, inPoint: number) {
    const startElement = xpath.select('ClipTrackItem/TrackItem/End', trackItem, true) as Element;
    startElement.textContent = `${Math.round(inPoint * premiereTicksPerSecond)}`;
  }
}

export function getSingleURef(node: Node, uniqueRefElementPath: string): string {
  const ref = xpath.select(uniqueRefElementPath + '/@ObjectURef', node, true) as Attr | undefined;
  if (!ref) {
    throw new Error('Missing ' + uniqueRefElementPath + ' UID reference for ' + node);
  }
  return ref.value;
}

export function getSequenceDuration(tracks: Track[]): number {
  let duration = 0;
  for (const track of tracks) {
    for (const clip of track.clips) {
      if (clip.end !== undefined && clip.end > duration) {
        duration = clip.end;
      }
    }
  }
  return duration;
}


function getClipFilePathText(mediaEl: Element): Element {
  return xpath.select('FilePath', mediaEl, true) as Element;
}

/**
 * Doc reference: https://ppro-scripting.docsforadobe.dev/other/time.html?highlight=254016000000#time-object
 */
export const premiereTicksPerSecond = 254016e6;

function parsePremiereTimeSpan(node: Node, xsubPath: string) {
  /*
    This constant is described in Adobe Premiere CEF examples such as:
    https://github.com/Adobe-CEP/Samples/blob/f86975c3689e29df03e7d815c3bb874045b7f991/PProPanel/jsx/PPRO/Premiere.jsx#L743
  */
  const premiereTicksPerMillisecond = premiereTicksPerSecond / 1000;
  const precision = 1e11;
  const text = xpath.select(xsubPath, node, true) as Node;
  const millis = parseInt(text.textContent ?? '0') / premiereTicksPerMillisecond;
  return Math.floor(millis * precision) / precision;
}

interface TrackType {
  mediaType: MediaType;
  labelPrefix: string;
  namePrefix: string;
}

const trackTypesByGroupTag: Record<string, TrackType> = {
  AudioTrackGroup: {
    mediaType: MediaType.AUDIO,
    labelPrefix: 'A',
    namePrefix: 'Audio '
  },
  VideoTrackGroup: {
    mediaType: MediaType.VIDEO,
    labelPrefix: 'V',
    namePrefix: 'Video '
  }
};

export function parsePrprojXml(xml: string): PrprojDocument {
  const doc: Document = new DOMParser().parseFromString(xml, 'application/xml');
  return new PrprojDocument(doc);
}

export function tryGetClipProjectItemProperties(cpi: Element): Element | null {
  const propertiesEl = xpath.select('ProjectItem/Node/Properties', cpi, true) as Element | undefined;
  if (!propertiesEl) {
    return null;
  }
  return propertiesEl;
}


export function getClipProjectItemProperties(cpi: Element): Element {
  const propertiesEl = xpath.select('ProjectItem/Node/Properties', cpi, true) as Element | undefined;
  if (!propertiesEl) {
    throw new Error('Missing Properties for ' + cpi);
  }
  return propertiesEl;
}

export function getObjectUid(el: Element): string {
  const result = el.getAttribute('ObjectUID');
  invariant(typeof result == 'string' && result, 'Expected ObjectUID attribute string');
  return result;
}

export function getObjectId(el: Element): string {
  const result = el.getAttribute('ObjectID');
  invariant(typeof result == 'string' && result, 'Expected ObjectUID attribute string');
  return result;
}

export function getOptionalElementTextContent(root: Element, refElementPath: string): string | null {
  const el = xpath.select(refElementPath, root, true) as Element;
  if (el == null) {
    return null;
  }
  return el.textContent;
}

export function getElementTextContent(root: Element, refElementPath: string): string {
  const el = xpath.select(refElementPath, root, true) as Element;
  if (el == null) {
    throw new Error('Expected to find element at ' + elementPath(root) + '/./' + refElementPath);
  }
  const text = el.textContent;
  invariant(typeof text == 'string', 'Expected element string content');
  return text;
}

export function getElementIntegerContent(root: Element, refElementPath: string): number {
  const text = getElementTextContent(root, refElementPath);
  const result = parseInt(text);
  invariant(!isNaN(result), 'Expected element integer content');
  return result;
}

export function getOptionalElementIntegerContent(root: Element, refElementPath: string): number | null {
  const el = xpath.select(refElementPath, root, true) as Element;
  if (!el) {
    return null;
  }
  const text = el.textContent;
  invariant(typeof text == 'string', 'Expected element string content');
  const result = parseInt(text);
  invariant(!isNaN(result), 'Expected element integer content');
  return result;
}

function elementPath(leaf: Element) {
  const rpath: string[] = [];
  let el: Element | null = leaf;
  while (el != null) {
    let comp = el.tagName;
    const objectId = el.getAttribute('ObjectID');
    if (objectId) {
      comp += '[@ObjectID=\'' + objectId + '\']';
    }
    rpath.push(comp);
    el = el.parentElement;
  }

  return rpath.reverse().join('/');
}
